import { ChartThemeProvider } from '@seek/charts';
import { Box, Column, Columns, Text } from 'braid-design-system';
import type React from 'react';
import {
  createContext,
  forwardRef,
  type ForwardRefRenderFunction,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type AnalyticalEvents,
  type RoleRequirementsEvent,
  getAvailableEvents,
} from '../../analytics';
import { tagLogs, captureException } from '../../errorLogging';
import { type Brand, type Features, getFeatures } from '../../features';
import {
  type ApiConfig,
  type SelectedQuestion,
  configure,
  trackWidgetInit,
  getExperiments,
} from '../../sdk';
import { Application, type RenderQuestionsTile } from '../App';
import { ConfigProvider } from '../Config/ConfigProvider';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { ExperimentProvider } from '../Experiment/ExperimentProvider';
import { FeatureFlagProvider } from '../FeatureFlag/FeatureFlagProvider';
import { HotjarProvider } from '../Hotjar/HotjarProvider';
import { IconJobStreet } from '../Icons';
import { JobDetailsProvider } from '../JobDetails/JobDetailsProvider';
import type { JobDetails } from '../Questions/Questions';
import type { Widget } from '../types';

interface RoleRequirementsConfig extends ApiConfig {
  /** Supported Vocab languages */
  locale:
    | 'en'
    | 'en-AU'
    | 'en-NZ'
    | 'en-ID'
    | 'en-MY'
    | 'en-PH'
    | 'en-SG'
    | 'id-ID'
    | 'th-TH';
  brand: Brand;
}

interface Props {
  questionnaireId: string;
  config: RoleRequirementsConfig;
  jobDetails?: JobDetails;
  onError?: (error: Error) => void;
  onChange?: (selections: SelectedQuestion[]) => void;
  onEvent?: (event: RoleRequirementsEvent) => void;
  /** Optional: listener that will trigger every time there is an update to selected questions */
  onSelectedQuestionsChange?: (questions: string[]) => void;
  /**
   * Trigger Hotjar Event handler. If this is not present, the widget will assume
   * that the hotjar is not enabled in browser and will not trigger any events.
   *
   * @param event The event name to trigger in Hotjar
   */
  triggerHotjarEvent?: (event: string) => void;
  /** Optional: localised help message to show users when an error panel displays */
  helpInformation?: React.ReactNode;
  /** Optional: overrides any brand and country features */
  features?: Partial<Features>;
  /** Optional: render prop to customise question tile */
  renderQuestionsTile?: RenderQuestionsTile;
}

export const AnalyticsContext = createContext<AnalyticalEvents>(
  getAvailableEvents(() => 0, false),
);

const defaultRender =
  (jobStreetIcon?: boolean) => (children: React.ReactNode) =>
    jobStreetIcon ? (
      <Box>
        <Columns space="none">
          <Column width="content">
            <Box paddingRight="small">
              <Text>
                <IconJobStreet />
              </Text>
            </Box>
          </Column>
          <Column>{children}</Column>
        </Columns>
      </Box>
    ) : (
      <Box>{children}</Box>
    );

const RoleRequirements: ForwardRefRenderFunction<Widget, Props> = (
  { config, features, onEvent = () => 0, renderQuestionsTile, ...props },
  ref,
) => {
  if (!config) {
    throw new Error('Missing config');
  }

  const analytics = useRef(
    getAvailableEvents(onEvent, features?.unifiedAnalytics ?? false),
  );

  const [experiments, setExperiments] = useState<string[]>([]);
  const [isExperimentLoaded, setIsExperimentLoaded] = useState(false);

  useEffect(() => {
    getExperiments()
      .then((response) => {
        analytics.current.setTestingId(response.testingId);
        setExperiments(response.experiments);
      })
      .catch((err) => {
        captureException(err);
      })
      .finally(() => {
        setIsExperimentLoaded(true);
      });
  }, [props.questionnaireId]);

  useEffect(() => {
    trackWidgetInit();
  }, []);

  useEffect(() => {
    configure(config);
    tagLogs('questionnaireId', props);
    tagLogs('environment', config);
    tagLogs('country', config);
    tagLogs('brand', config);
    tagLogs('type', { type: 'oidc' });
    tagLogs('locale', config);
    tagLogs('advertiserId', config.auth);
  }, [config, props]);

  return (
    <ErrorBoundary {...props}>
      <ConfigProvider config={config}>
        <ChartThemeProvider>
          <JobDetailsProvider jobDetails={props.jobDetails}>
            <FeatureFlagProvider features={getFeatures(config, features)}>
              <AnalyticsContext.Provider value={analytics.current}>
                <ExperimentProvider experiments={experiments}>
                  <HotjarProvider triggerHotjarEvent={props.triggerHotjarEvent}>
                    <Application
                      ref={ref}
                      {...props}
                      renderQuestions={
                        renderQuestionsTile ??
                        defaultRender(features?.jobStreetIcon)
                      }
                      isExperimentLoaded={isExperimentLoaded}
                    />
                  </HotjarProvider>
                </ExperimentProvider>
              </AnalyticsContext.Provider>
            </FeatureFlagProvider>
          </JobDetailsProvider>
        </ChartThemeProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

RoleRequirements.displayName = 'RoleRequirements';

const ForwardedRefRoleRequirements = forwardRef(RoleRequirements);

export {
  ForwardedRefRoleRequirements as RoleRequirements,
  type RoleRequirementsEvent,
  type RoleRequirementsConfig,
};
